/* RESPONSIBLE TEAM: team-phone */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ajax from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';
import type AppService from 'embercom/services/app-service';

interface PhoneEditorMetadataResponse {
  step_exclusions_by_trigger: Record<string, string[]>;
}

// Steps and triggers arrive snake_case from the backend but are addressed
// kebab-case on the frontend; normalise both before comparing.
function normalize(value: string): string {
  return value.replace(/-/g, '_');
}

// Looks up which workflow steps the backend has excluded for a phone-eligible
// trigger. The backend (StepEditorMetadata.exclusions_by_trigger) owns the rule
// and precomputes the per-trigger exclusion lists; this service fetches that map
// once per workspace and answers each question as a set-membership check.
export default class PhoneWorkflowStepScopingService extends Service {
  @service declare appService: AppService;

  @tracked private exclusionsByTrigger: Record<string, string[]> = {};
  @tracked loaded = false;
  // The workspace the loaded map belongs to, so a workspace switch re-fetches
  // rather than scoping steps with another app's exclusions.
  private loadedAppId?: string;
  private loadingAppId?: string;

  // Safe to call from a reactive getter on every recompute — only the first call
  // per workspace hits the network.
  ensureLoaded(): void {
    let appId = this.appService.app.id;
    if (appId === this.loadingAppId || appId === this.loadedAppId) {
      return;
    }
    this.loaded = false;
    this.loadingAppId = appId;
    this.load(appId);
  }

  private async load(appId: string): Promise<void> {
    try {
      let response: PhoneEditorMetadataResponse = await ajax({
        url: '/ember/operator_workflows/phone_editor_metadata',
        type: 'GET',
        data: { app_id: appId },
      });

      // Drop a response that a later workspace switch has superseded, so a
      // slow fetch for an old workspace can't overwrite the current one.
      if (appId !== this.loadingAppId) {
        return;
      }

      let exclusions: Record<string, string[]> = {};
      Object.entries(response.step_exclusions_by_trigger ?? {}).forEach(([trigger, steps]) => {
        exclusions[normalize(trigger)] = (steps ?? []).map(normalize);
      });

      this.exclusionsByTrigger = exclusions;
      this.loadedAppId = appId;
      this.loadingAppId = undefined;
      this.loaded = true;
    } catch (error: unknown) {
      // Fail open: with no map, the per-step channel matrix stays the backstop.
      // Only clear the in-flight marker if this request is still the current one,
      // so a stale failure can't unblock a newer workspace's fetch.
      if (appId === this.loadingAppId) {
        this.loadingAppId = undefined;
      }
      captureException(error, { fingerprint: ['phone-workflow-step-scoping', 'load'] });
    }
  }

  // A step is allowed unless the backend excluded it for this trigger. Fails open
  // until the current workspace's map has loaded, and for any unlisted trigger.
  isStepAllowedOnPhone(stepType: string, triggerType: string): boolean {
    if (!this.loaded || this.loadedAppId !== this.appService.app.id) {
      return true;
    }

    let excluded = this.exclusionsByTrigger[normalize(triggerType)];
    return excluded ? !excluded.includes(normalize(stepType)) : true;
  }
}

declare module '@ember/service' {
  interface Registry {
    phoneWorkflowStepScoping: PhoneWorkflowStepScopingService;
    'phone-workflow-step-scoping': PhoneWorkflowStepScopingService;
  }
}
